import { isAfter } from 'date-fns';
import { PublicAuthToken } from '@api/shared-types/PublicAuthToken';
import { isPublicAuthToken } from '@features/authentication/typeGuards';



export const TOKEN_KEY_PREFIX = 'lfmt.token.';


const hasLocalStorage = () => {
	try {
		const storage = window.localStorage;
		const testKey = 'localStorageTest'
		
		storage.setItem(testKey, 'test')
		storage.getItem(testKey)
		storage.removeItem(testKey)
	}
	catch (error) {
		console.error('Authentication Error: Local storage not found or supported');
		console.error(`Details:\nError: ${error}\nEnvironment: ${process.env.ENV}`);
		return false
	}
	return true
}


const initStorage = () => {

	if (hasLocalStorage()) {
		const newStorage = window.localStorage;
		return newStorage;
	}
	return null;
};


// storage instance
const storage = initStorage();



export const setPublicAuthToken = (token: PublicAuthToken): void => {
	try {
		if (!storage) {
			console.error('Authentication Error: Local storage not found or supported');
			console.error(`Details:\nEnvironment: ${process.env.ENV}`);
			return;
		}

		if (!isPublicAuthToken(token)) {
			console.error('Authentication Error: Object is not PublicAuthToken');
			console.error(`Details:\nEnvironment: ${process.env.ENV}`);
			return;
		}

		if (!validatePublicAuthToken(token)) {
			console.error('Authentication Error: PublicAuthToken is not valid');
			console.error(`Details:\nEnvironment: ${process.env.ENV}`);
			return;
		}

		let localStorageKey = `${TOKEN_KEY_PREFIX}${token.userId}`;
		let localStorageValue = JSON.stringify(token);
		
		storage.setItem(localStorageKey, localStorageValue);
		
		window.dispatchEvent(new StorageEvent("auth-token-update", {
			key: localStorageKey,
			newValue: localStorageValue,
		}));
	}
	catch (error) {
		console.error('Authentication Error: Failed to set PublicAuthToken');
		console.error(`Details:\nError: ${error}\nEnvironment: ${process.env.ENV}`);
	}
}



export const getPublicAuthToken = (userId: string): PublicAuthToken | null => {
	try {
		if (!storage) {
			console.error('Authentication Error: Local storage not found or supported');
			console.error(`Details:\nEnvironment: ${process.env.ENV}`);
			return null;
		}
		if (!userId) {
			console.error('Authentication Error: User ID not found');
			console.error(`Details:\nEnvironment: ${process.env.ENV}`);
			return null;
		}
		
		const stringToken = storage.getItem(`${TOKEN_KEY_PREFIX}${userId}`);
		if (!stringToken) return null;

		const parsedToken = parsePublicAuthToken(stringToken);
		const isValid = validatePublicAuthToken(parsedToken, { isFromStorage: true });
		if (!isValid) return null;

		return parsedToken;
	}
	catch (error) {
		console.error('Authentication Error: Failed to get PublicAuthToken');
		console.error(`Details:\nError: ${error}\nEnvironment: ${process.env.ENV}`);
		return null;
	}
}



export const findPublicAuthToken = (): PublicAuthToken | null => {
	try {
		if (!storage) {
			console.error('Authentication Error: Local storage not found or supported');
			console.error(`Details:\nEnvironment: ${process.env.ENV}`);
			return null;
		}
			
		for (const key of Object.keys(storage)) {
			if (!key || !key.startsWith(TOKEN_KEY_PREFIX)) continue;

			const stringToken = storage.getItem(key)
			if (!stringToken) continue;

			const parsedToken = parsePublicAuthToken(stringToken);
			const isValid = validatePublicAuthToken(parsedToken, { isFromStorage: true });
			if (!isValid) continue;

			window.dispatchEvent(new StorageEvent("auth-token-update", {
				key: key,
				newValue: stringToken,
			}));

			return parsedToken;
		}

		return null;
	}
	catch (error) {
		console.error('Authentication Error: Failed to find PublicAuthToken');
		console.error(`Details:\nError: ${error}\nEnvironment: ${process.env.ENV}`);
		return null;
	}
}



export const deletePublicAuthToken = (userId: string): void => {
	try {
		if (!storage) {
			console.error('Authentication Error: Local storage not found or supported');
			console.error(`Details:\nEnvironment: ${process.env.ENV}`);
			return;
		}
		if (!userId) {
			console.error('Authentication Error: User ID not found');
			console.error(`Details:\nEnvironment: ${process.env.ENV}`);
			return;
		}
			
		const localStorageKey = `${TOKEN_KEY_PREFIX}${userId}`;
	
		storage.removeItem(localStorageKey);

		window.dispatchEvent(new StorageEvent("auth-token-update", {
			key: localStorageKey,
			newValue: null,
		}));
	}
	catch (error) {
		console.error('Authentication Error: Failed to delete PublicAuthToken');
		console.error(`Details:\nError: ${error}\nEnvironment: ${process.env.ENV}`);
	}
}



export const deleteAllPublicAuthTokens = (): void => {
	try {
		if (!storage) {
			console.error('Authentication Error: Local storage not found or supported');
			console.error(`Details:\nEnvironment: ${process.env.ENV}`);
			return;
		}
			
		let allTokenKeys = [];
	
		for (const key of Object.keys(storage)) {
			if (key && key.startsWith(TOKEN_KEY_PREFIX)) {
				allTokenKeys.push(key);
			}
		}

		if (allTokenKeys && allTokenKeys.length > 0) {
			for (const keyForDelete of allTokenKeys) {
				storage.removeItem(keyForDelete);
			}
		}
		
		window.dispatchEvent(new StorageEvent("auth-token-delete-all"));
	}
	catch (error) {
		console.error('Authentication Error: Failed to delete all PublicAuthTokens');
		console.error(`Details:\nError: ${error}\nEnvironment: ${process.env.ENV}`);
	}
}



export const parsePublicAuthToken = (publicAuthToken: string | null | undefined): PublicAuthToken | null => {
	try {
		if (!publicAuthToken) return null;

		let parsedPublicToken = JSON.parse(publicAuthToken);

		if (!isPublicAuthToken(parsedPublicToken)) {
			console.error('Authentication Error: Parsed value is not PublicAuthToken');
			console.error(`Details:\nEnvironment: ${process.env.ENV}`);
			return null;
		}
		
		return parsedPublicToken;
	}
	catch (error) {
		console.error('Authentication Error: Failed to parse PublicAuthToken');
		console.error(`Details:\nError: ${error}\nEnvironment: ${process.env.ENV}`);
		return null;
	}
};



export const validatePublicAuthToken = (publicAuthToken: PublicAuthToken | null | undefined, options?: { isFromStorage: boolean }): boolean => {
	try {
		if (!publicAuthToken) return false;

		const isTokenValid = isAfter(publicAuthToken.validTo, new Date().toISOString());

		if (!isTokenValid && options && options.isFromStorage) {
			deletePublicAuthToken(publicAuthToken.userId);
			return false;
		}

		const isScopeValid = publicAuthToken.scope.some(x => x.app === process.env.AUTH_APP);

		return isTokenValid && isScopeValid;
	}
	catch (error) {
		console.error('Authentication Error: Failed to parse validTo property of PublicAuthToken');
		console.error(`Details:\nError: ${error}\nEnvironment: ${process.env.ENV}`);
		return false;
	}
};